import { useCallback, useEffect, useRef } from "react";

export type LongPressOptions = {
  onLongPress: (target: HTMLElement) => void;
  thresholdMs?: number;
  movementPx?: number;
};

type TouchHandlers = {
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchCancel: (e: React.TouchEvent<HTMLElement>) => void;
  onContextMenu: (e: React.MouseEvent<HTMLElement>) => void;
};

const useLongPress = ({
  onLongPress,
  thresholdMs = 450,
  movementPx = 10,
}: LongPressOptions): TouchHandlers => {
  const timerRef = useRef<number | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const triggeredRef = useRef(false);
  const targetRef = useRef<HTMLElement | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
    targetRef.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (e.touches.length !== 1) {
        clear();
        return;
      }
      triggeredRef.current = false;
      const touch = e.touches[0];
      startPosRef.current = { x: touch.clientX, y: touch.clientY };
      targetRef.current = e.currentTarget;
      timerRef.current = window.setTimeout(() => {
        if (targetRef.current) {
          triggeredRef.current = true;
          onLongPress(targetRef.current);
        }
      }, thresholdMs);
    },
    [clear, onLongPress, thresholdMs]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!startPosRef.current || timerRef.current === null) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = Math.abs(touch.clientX - startPosRef.current.x);
      const dy = Math.abs(touch.clientY - startPosRef.current.y);
      if (dx > movementPx || dy > movementPx) {
        clear();
      }
    },
    [clear, movementPx]
  );

  const onTouchEnd = useCallback(() => {
    clear();
  }, [clear]);

  const onTouchCancel = useCallback(() => {
    clear();
  }, [clear]);

  const onContextMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // Suppress the system context menu (Android long-press) so our custom
    // menu is the only one shown.
    e.preventDefault();
  }, []);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    onContextMenu,
  };
};

export default useLongPress;
